import { defineStore } from "pinia";

import { cardSizeFrom } from "@/board/board-utils";
import { captureMessage } from "@/error/sentry";
import { relativeFromViewport } from "@/math/coordinate-utils";
import type { BoardType, Id } from "@/model/baseTypes";
import { boardTitle } from "@/model/baseTypes";
import type { Board, BoardData, BoardId, IterationStatus } from "@/model/board";
import { isFrontendBoard, isSolutionBoard } from "@/model/board";
import type { BoardCard, Card } from "@/model/card";
import type { RelativeCoordinate, ViewportCoordinate } from "@/model/coordinates";
import type { FlexType } from "@/model/flexboard";
import type { Art } from "@/model/session";
import type { Shape } from "@/model/shape";
import type { StickyType } from "@/model/stickyType";
import { useGlobalI18n } from "@/translations/i18n";

import { useArtStore } from "./art";
import { useBoardStore } from "./board";
import { animateCardMove } from "./helpers/animator";
import type { BoardByTypeOptions } from "./helpers/boardByType";
import { boardByType, boardByTypeOptional } from "./helpers/boardByType";
import { flexBoard, objectivesBoard, objectivesBoardId } from "./helpers/frontendBoard";
import { mirrorableToNonFlexBoard } from "./helpers/mirror";
import { useIterationStore } from "./iteration";
import { useTeamStore } from "./team";
import { useUserStore } from "./user";

type BoardMap = Record<Board["id"], Board>;

const { tt } = useGlobalI18n();

export const useBoardsStore = defineStore("boards", {
  state: () => ({
    boards: { objectives: objectivesBoard() } as BoardMap,
  }),
  getters: {
    findIteration: (state) => {
      return (cardId: keyof Board["cards"], iterId: number) => {
        for (const board of Object.values(state.boards)) {
          if (board.type === "team" && board.cards[cardId]) {
            return useIterationStore().iteration(board, iterId);
          }
        }
      };
    },
    boardById: (state) => {
      return (boardId: Board["id"]): Board => {
        const board = state.boards[boardId];
        if (!board) {
          captureMessage("Could not find board", { info: { boardId } });
          return { id: boardId } as Board;
        }
        return board;
      };
    },
    boardByType: (state) => {
      return <T extends BoardType>(type: T, options: BoardByTypeOptions = {}) =>
        boardByType(state.boards, type, options);
    },
    boardByTypeOptional: (state) => {
      return <T extends BoardType>(type: T, options: BoardByTypeOptions = {}) =>
        boardByTypeOptional(state.boards, type, options);
    },
    stickyTypeOriginBoard() {
      return (type: StickyType, pos: ViewportCoordinate) => {
        // Exclude teamId if it belongs to an external team, as external teams do not have associated boards
        const rawTeamId = useBoardStore().positionalCardProperties(
          relativeFromViewport(pos),
        ).teamId;
        const teamId =
          rawTeamId && !useTeamStore().isExternalTeam(rawTeamId) ? rawTeamId : undefined;
        return this.boardByType(type.origin, { teamId });
      };
    },
    planningBoards() {
      return (artLevelOnly = false) => [
        this.boardByType("program"),
        ...(!artLevelOnly && this.hasSolutionBoard ? [this.boardByType("solution")] : []),
      ];
    },
    artBoardsToLoad() {
      return (force = false) =>
        [...this.currentArtBoards, ...this.solutionBoards]
          .filter((board) => {
            const wasLoaded = board.state.state !== "inited" && !force;
            return board !== useBoardStore().currentBoard() && !wasLoaded;
          })
          .sort((boardA, boardB) => order(boardA) - order(boardB));

      function order(board: Board) {
        const art = board.artId === useArtStore().current.id ? 0 : 5;
        const type = board.type === "backlog" ? 0 : board.type === "team" ? 1 : 2;
        return art + type;
      }
    },
    artTeamBoards: (state) => {
      const teamBoards = Object.values(state.boards).filter(
        (board): board is BoardData<"team"> => board.type === "team",
      );
      return (artId?: Art["id"]) =>
        teamBoards.filter((board) =>
          artId ? board.team.artId === artId : useArtStore().isCurrentArt(board.team.artId),
        );
    },
    currentArtBoards: (state) =>
      Object.values(state.boards).filter(
        (board) => (board.artId || "") === (useArtStore().current.id || ""),
      ),
    solutionBoards: (state) =>
      Object.values(state.boards).filter((board) => isSolutionBoard(board.type)),
    boardsInited: (state) =>
      Object.values(state.boards).some((board) => !isFrontendBoard(board.type)),
    backlogStickies(): BoardCard[] {
      return Object.values({
        ...this.boardByType("backlog").cards,
        ...(this.hasSolutionBacklogBoard ? this.boardByType("solution_backlog").cards : {}),
      });
    },
    hasSolutionBacklogBoard: (state) =>
      Object.values(state.boards).some((board) => board.type === "solution_backlog"),
    hasSolutionBoard: (state) =>
      Object.values(state.boards).some((board) => board.type === "solution"),
    mirrorTargetBoards: (state) => {
      return (cards: Card[]) =>
        Object.values(state.boards).filter(
          (board) => !isCurrentBoard(board) && mirrorableToNonFlexBoard(cards, board),
        );
    },
    boardTitle() {
      return (id?: string) => {
        const board = id ? this.boardById(id) : useBoardStore().board;
        return board ? tt(boardTitle(board, useArtStore().artById(board.artId))) : "";
      };
    },
    teamBoardByTeamId: (state) => {
      return (teamId: string) =>
        Object.values(state.boards).find(
          (board): board is BoardData<"team"> => board.type === "team" && board.team.id === teamId,
        );
    },
    boardTypeOrder: () => {
      const order: Record<string, number> = {
        solution_backlog: 0,
        solution: 1,
        backlog: 2,
        program: 3,
        objectives: 4,
        risk: 5,
        team: 6,
        flex: 7,
      };
      return (a: Board, b: Board) => (order[a.type] ?? 99) - (order[b.type] ?? 99);
    },
    boardByOriginCard: function () {
      return (card: Card): Board | undefined => {
        const originBoardType = card.type.origin;
        const teamAndArt = { teamId: card.teamId || undefined, artId: card.artId || undefined };
        return this.boardByTypeOptional(originBoardType, teamAndArt);
      };
    },
  },
  actions: {
    addBoards(boards: BoardMap) {
      this.boards = { ...this.boards, ...boards };
    },
    addBoard<T extends BoardType>(board: BoardData<T>): BoardData<T> {
      this.boards = { ...this.boards, [board.id]: board };
      return board;
    },
    addFlexBoard(id: string, name: string, flexType: FlexType) {
      return this.addBoard<"flex">(flexBoard(id, name, flexType));
    },
    removeFlexBoard(id: string) {
      delete this.boards[id];
    },
    updateObjectiveBoardId() {
      Object.values(this.boards).forEach((board) => {
        if (board.type === "objectives") {
          board.id = objectivesBoardId();
        }
      });
    },
    updateBoards<T extends BoardType>(
      type: T,
      boards: Array<BoardData<T>>,
      update: (exist: BoardData<T>, board: BoardData<T>) => void,
    ) {
      const bs = this.boards;
      for (const id in bs) {
        if (bs[id].type === type && !boards.find((board) => board.id === bs[id].id)) {
          delete this.boards[id];
        }
      }
      boards.forEach((board) => {
        const found = this.boards[board.id] as BoardData<T>;
        if (found) {
          update(found, board);
        } else {
          this.boards[board.id] = board;
        }
      });
    },
    setVelocity(e: { id: string; velocity: number; iteration: number }) {
      const board = this.boards[e.id] as BoardData<"team">;
      useIterationStore().iteration(board, e.iteration).velocity = e.velocity;
    },
    setCardSize(e: BoardId & { factor: number }) {
      if (
        !useUserStore().isAllowed("edit") ||
        !useUserStore().isNonTeamZoomAllowed(useBoardStore().currentBoard())
      ) {
        return false;
      }

      if (!(e.boardId in this.boards)) {
        // Board is not present: it could be a flex board, which is not loaded
        // until it is visited for the first time. However, we could receive an
        // update from someone else on the board. Since the flex board content
        // will be loaded the first time we access it, it's safe to ignore the
        // event.
        return false;
      }
      this.boards[e.boardId].cardSize = cardSizeFrom(e);
      return true;
    },
    setIterationStatus(e: {
      boardId?: string;
      id: number;
      status: IterationStatus;
      detail?: string;
    }) {
      const boardId = e.boardId || useBoardStore().currentBoard().id;
      const board = this.boards[boardId] as BoardData<"team">;
      const iter = board.iterationStates[e.id];
      if (iter) {
        iter.status = e.status;
        iter.detail = e.detail || null;
        if (e.status === "success") {
          setTimeout(() => this.setIterationStatus({ boardId, id: e.id, status: "synced" }), 5000);
        }
      }
    },
    cardToFront(e: Id & Partial<BoardId> & { zIndex?: number }) {
      const board = e.boardId ? this.boards[e.boardId] : useBoardStore().board;
      if (!board) {
        // Board is not present: it could be a flex board, which is not loaded
        // until it is visited for the first time. However, we could receive an
        // update from someone else on the board. Since the flex board content
        // will be loaded the first time we access it, it's safe to ignore the
        // event.
        return false;
      }
      const boardCard = board.cards[e.id];
      if (boardCard) {
        // card might just have been deleted
        boardCard.meta.zIndex = e.zIndex || ++board.maxZ;
        return true;
      }
    },
    setCardPos(e: Id & BoardId & RelativeCoordinate, endFn?: () => void) {
      const boardCard = this.boards[e.boardId].cards[e.id];
      animateCardMove(
        boardCard,
        e,
        (pos) => {
          boardCard.meta.pos.x = pos.x;
          boardCard.meta.pos.y = pos.y;
        },
        endFn,
      );
    },
    addShape(boardId: string, shape: Shape) {
      const { shapes, index } = this.findShape(boardId, shape.id);
      if (index >= 0) {
        return shapes[index];
      }
      shapes.push(shape);
      return shape;
    },
    editShape(boardId: string, shape: Shape) {
      const { shapes, index } = this.findShape(boardId, shape.id);
      if (index >= 0) {
        shapes[index] = shape;
      }
    },
    removeShape(boardId: string, id: string) {
      const { shapes, index } = this.findShape(boardId, id);
      if (index >= 0) {
        shapes.splice(index, 1);
      }
    },
    findShape(boardId: string, id: string) {
      const shapes = this.boards[boardId]?.shapes;
      return { shapes, index: shapes.findIndex((s) => s.id === id) };
    },
  },
});

function isCurrentBoard(board: Board) {
  return board.id === useBoardStore().board?.id;
}
